import { useContext } from "react";
import { ConfiguratorContext } from "../context";
import usePersonalData from "./usePersonalData";

/**
 * Hook para datos personales específicos de Kinto One
 * Maneja configuración de meses, pie, kilometraje y opciones disponibles
 */
const useKintoOnePersonalData = () => {
    const [state, setState] = useContext(ConfiguratorContext);
    const { isCompany } = usePersonalData();

    // Los textos comerciales vienen duplicados por producto en KintoCar:
    // empresa usa los *_one_business, persona natural los *_one_personal
    const productSuffix = () => (isCompany() ? "one_business" : "one_personal");

    // Establece los datos iniciales del vehículo para Kinto
    const setKintoOnePersonalData = (kintoCarData) => {
        setState((prevState) => ({
            ...prevState,
            kinto_one_personal: {
                ...prevState.kinto_one_personal,
                enabled: Boolean(kintoCarData),
                car_data: kintoCarData,
                values: kintoCarData?.values || [],
            },
        }));
    };

    // Actualiza la configuración seleccionada de Kinto
    const updateKintoConfiguration = (months, downpayment, kilometers) => {
        const { values } = state.kinto_one_personal;

        if (!values) return;

        setState((prevState) => ({
            ...prevState,
            kinto_one_personal: {
                ...prevState.kinto_one_personal,
                selected_months: months,
                selected_downpayment: downpayment,
                selected_kilometers: kilometers,
            },
        }));
    };

    const getAvailableOptions = () => {
        const { car_data, values } = state.kinto_one_personal;

        if (!car_data || !values) {
            return {
                months: [12, 24, 36, 48],
                kilometers: [10000, 12000, 15000, 18000, 20000, 24000],
            };
        }

        // Extract unique values from the car values
        const uniqueMonths = [...new Set(values.map((v) => v.months))].sort();
        const uniqueDownpayments = [...new Set(values.map((v) => v.downpayment))].sort();
        const uniqueKilometers = [...new Set(values.map((v) => v.kilometers))].sort();

        return {
            months: uniqueMonths.length > 0 ? uniqueMonths : [12, 24, 36, 48],
            downpayments: uniqueDownpayments.length > 0 ? uniqueDownpayments : [0, 20],
            kilometers: uniqueKilometers.length > 0 ? uniqueKilometers : [10000, 12000, 15000, 18000, 20000, 24000],
        };
    };

    const getCurrentKintoData = () => {
        return state.kinto_one_personal;
    };

    const isKintoEnabled = () => {
        return state.kinto_one_personal.enabled && state.kinto_one_personal.car_data;
    };

    const getKintoDisclaimer = () => {
        const { car_data } = state.kinto_one_personal;
        return car_data?.[`featured_disclaimer_${productSuffix()}`] || "";
    };

    const getKintoTagText = () => {
        const { car_data } = state.kinto_one_personal;
        const suffix = productSuffix();
        return {
            text: car_data?.[`tag_text_${suffix}`] || "",
            style: car_data?.[`tag_text_${suffix}_style`] || "DE0000",
        };
    };

    const getKintoSliderTagText = () => {
        const { car_data } = state.kinto_one_personal;
        const suffix = productSuffix();
        return {
            text: car_data?.[`slider_tag_text_${suffix}`] || "",
            style: car_data?.[`slider_tag_text_${suffix}_style`] || "DE0000",
        };
    };

    // Guarda la última respuesta de la API de Kinto. Si se indica `versionId`
    // la respuesta se descarta cuando el usuario ya cambió de versión, para no
    // dejar la amortización de un vehículo distinto al configurado.
    const setKintoApiResponse = (apiResponse, versionId = undefined) => {
        setState((prevState) => {
            if (versionId !== undefined && prevState.selectedVersion?.id !== versionId) {
                return prevState;
            }

            return {
                ...prevState,
                kinto_one_personal: {
                    ...prevState.kinto_one_personal,
                    api_response: apiResponse,
                },
            };
        });
    };

    const getKintoApiResponse = () => {
        return state.kinto_one_personal.api_response;
    };

    // Al cambiar de persona natural a empresa (o viceversa) cambia el producto
    // Kinto: la cuota simulada deja de corresponder y un lead ya creado
    // pertenece al producto anterior, así que se descartan ambos.
    const resetKintoForProductChange = () => {
        setState((prevState) => ({
            ...prevState,
            kinto_one_personal: {
                ...prevState.kinto_one_personal,
                api_response: null,
            },
            kintoSimulationData: {
                ...prevState.kintoSimulationData,
                hasResult: false,
                resultData: null,
                isLoading: false,
                error: null,
                simulated_for: null,
            },
            simulationResults: {
                ...prevState.simulationResults,
                3: false,
            },
            kinto_lead_created: false,
            kinto_lead_id: null,
            kinto_lead_type: null,
        }));
    };

    return {
        resetKintoForProductChange,
        setKintoOnePersonalData,
        updateKintoConfiguration,
        getAvailableOptions,
        getCurrentKintoData,
        isKintoEnabled,
        getKintoDisclaimer,
        getKintoTagText,
        getKintoSliderTagText,
        setKintoApiResponse,
        getKintoApiResponse,
    };
};

export default useKintoOnePersonalData;
